import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { YAreaChart } from './YAreaChart';
import {
  DEER_SIGHTINGS_X_KEY,
  buildDeerSightingsSeries,
  deerSightingsTickFormatter,
  deerSightingsLabelFormatter,
  formatDeerSightingsValue
} from './deerSightingsTimeline';

/**
 * Canonical deer sightings-over-time chart (area + gradient + optional brush).
 * Used on personal hunt stats (Home, Hunts) and club Stats tab.
 */
export function YDeerSightingsTimelineChart({
  data = [],
  height = 240,
  animate = true,
  series,
  legend = false,
  brush,
  brushSummary,
  brushActions,
  xTickFormatter = deerSightingsTickFormatter,
  tooltipLabelFormatter = deerSightingsLabelFormatter,
  tooltipValueFormatter = formatDeerSightingsValue,
  yAxis = { allowDecimals: false },
  dataTestId = 'deer-sightings-timeline-chart',
  gradientInstanceId = 'deer-sightings'
}) {
  const resolvedSeries = useMemo(
    () => series ?? buildDeerSightingsSeries({ animate }),
    [series, animate]
  );

  return (
    <div className="y-deer-sightings-timeline">
      <YAreaChart
        data={data}
        height={height}
        xKey={DEER_SIGHTINGS_X_KEY}
        series={resolvedSeries}
        legend={legend}
        xTickFormatter={xTickFormatter}
        tooltipLabelFormatter={tooltipLabelFormatter}
        tooltipValueFormatter={tooltipValueFormatter}
        yAxis={yAxis}
        brush={brush}
        dataTestId={dataTestId}
        gradientInstanceId={gradientInstanceId}
      />
      {brushSummary && (
        <div className="y-chart-range-summary">
          <span>{brushSummary}</span>
          {brushActions}
        </div>
      )}
    </div>
  );
}

YDeerSightingsTimelineChart.propTypes = {
  data: PropTypes.array,
  height: PropTypes.number,
  animate: PropTypes.bool,
  series: PropTypes.arrayOf(PropTypes.object),
  legend: PropTypes.bool,
  brush: PropTypes.shape({
    enabled: PropTypes.bool,
    selection: PropTypes.shape({
      startIndex: PropTypes.number,
      endIndex: PropTypes.number
    }),
    onChange: PropTypes.func,
    version: PropTypes.number
  }),
  brushSummary: PropTypes.string,
  brushActions: PropTypes.node,
  xTickFormatter: PropTypes.func,
  tooltipLabelFormatter: PropTypes.func,
  tooltipValueFormatter: PropTypes.func,
  yAxis: PropTypes.object,
  dataTestId: PropTypes.string,
  gradientInstanceId: PropTypes.string
};

export default YDeerSightingsTimelineChart;
